import React, { useState } from 'react';
import { passwordProblem, PASSWORD_MIN, PASSWORD_HINT } from '../../lib/passwordPolicy';
import { supabase, supabaseEnabled } from '../../lib/supabase';

// ──────────────────────────────────────────────────────────────────────
// LoginScreen — split-card Supabase email + password auth.
//
// Tabs: "Sign in" | "Create account". Defaults to "Create account" on
// first visit (most parents don't have an account yet). A third,
// tab-less "forgot password" state reuses the same right-panel layout.
//
// There is no activation-code or admin shortcut: access comes only from a signed-in account.
// ──────────────────────────────────────────────────────────────────────

const T = {
  bg:          '#EEEDF3',
  card:        '#FFFFFF',
  border:      '#ECEAF3',
  ink:         '#16162A',
  muted:       '#6B6A82',
  mutedLo:     '#8B87A8',
  labelInk:    '#4A4860',
  indigo:      '#6C5CE7',
  indigoDark:  '#5546C0',
  indigoDeep:  '#3A2E8C',
  indigoBtn:   '#5241B8',
  coral:       '#FF7A59',
  fieldBg:     '#FCFCFE',
  fieldBorder: '#E4E2EE',
  fieldTop:    '#F0EFF6',
  tileIndigo:  '#EDE8FF',
  tileCoral:   '#FFEDE6',
  tileGreen:   '#E4F5EC',
  green:       '#16A36B',
  error:       '#DC2626',
};

const label = {
  display: 'block', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600,
  fontSize: 12.5, color: T.labelInk, marginBottom: 6,
};
const field = {
  width: '100%', height: 50, border: `1.5px solid ${T.fieldBorder}`, borderRadius: 13,
  background: T.fieldBg, padding: '0 15px', fontSize: 14.5, color: T.ink,
  fontFamily: "'Hanken Grotesk', sans-serif", transition: 'all .18s ease', boxSizing: 'border-box',
};
const ghostLink = {
  background: 'none', border: 'none', color: T.indigo, fontWeight: 600, fontSize: 12,
  cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif", padding: 0,
};

function tabBtnStyle(active) {
  return {
    flex: 1, height: 40, border: 'none', borderRadius: 10, cursor: 'pointer',
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 14,
    background: active ? '#fff' : 'transparent',
    color: active ? T.ink : T.muted,
    boxShadow: active ? '0 1px 3px rgba(30,22,70,.10)' : 'none',
    transition: 'all .18s ease',
  };
}
function submitBtnStyle(disabled) {
  return {
    width: '100%', height: 52, marginTop: 22, border: 'none', borderRadius: 14,
    cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: "'Space Grotesk', sans-serif",
    fontWeight: 700, fontSize: 15.5, color: '#fff',
    background: disabled ? '#B6AEE0' : `linear-gradient(135deg, ${T.indigo}, ${T.indigoBtn})`,
    boxShadow: disabled ? 'none' : `0 8px 22px rgba(108,92,231,.32)`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
    boxSizing: 'border-box',
  };
}

const PROOF_POINTS = [
  {
    title: 'Hands-on interactives', desc: 'A different build in every lesson',
    icon: (
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
        <path d="M12 3 L21 8 v8 l-9 5 -9 -5 V8 Z" stroke="#fff" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M12 12 L21 8 M12 12 v9 M12 12 L3 8" stroke="rgba(255,255,255,.6)" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    title: 'Aligned to the curriculum', desc: 'Australian Curriculum v9',
    icon: (
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
        <path d="M4 13 l5 5 L20 6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Coaching as they go', desc: 'Named next steps, never just wrong',
    icon: (
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="8.5" stroke="#fff" strokeWidth="1.7" />
        <path d="M12 8 v4.4 l3 2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
];

const WHAT_YOU_GET = [
  {
    tile: T.tileIndigo, title: 'Interactive lessons',
    desc: 'Maths, English, Science, HASS and Technologies, with a build-and-test activity in every lesson',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M4 5.5 a2 2 0 0 1 2 -2 h5 v17 h-5 a2 2 0 0 0 -2 2 Z" fill={T.indigo} opacity=".9" />
        <path d="M20 5.5 a2 2 0 0 0 -2 -2 h-5 v17 h5 a2 2 0 0 1 2 2 Z" fill={T.indigo} opacity=".55" />
      </svg>
    ),
  },
  {
    tile: T.tileCoral, title: 'Exam practice with worked answers',
    desc: 'NAPLAN, Selective, OC Test, ACER, HSC and VCE, explained step by step',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <rect x="4" y="3" width="16" height="18" rx="2.5" fill={T.coral} opacity=".92" />
        <path d="M8 9 h8 M8 13 h8 M8 17 h5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    tile: T.tileGreen, title: 'Progress you can actually see',
    desc: 'Accuracy by subject, streaks and a shareable report for parents',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M4 19 V11 M10 19 V6 M16 19 V14 M22 19 h-20" stroke={T.green} strokeWidth="2.4" strokeLinecap="round" />
      </svg>
    ),
  },
];

export default function LoginScreen({ onAuthed }) {
  // First-time visitors land on Create account, per spec.
  const [tabKey, setTabKey] = useState('signup'); // signup | signin | forgot
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');


  const isCreate = tabKey === 'signup';
  const isSignIn = tabKey === 'signin';
  const isForgot = tabKey === 'forgot';

  const reset = () => { setError(''); setInfo(''); };

  const doSignIn = async () => {
    reset();
    if (!email || !password) { setError('Enter your email and password.'); return; }
    if (!supabaseEnabled) { setError("Sign-in isn't set up on this copy of the app yet."); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) { setError(humanError(error.message)); return; }
      if (data?.session) onAuthed?.(data.session);
    } finally { setLoading(false); }
  };

  const doSignUp = async () => {
    reset();
    if (!email || !password) { setError('Enter your email and choose a password.'); return; }
    const problem = passwordProblem(password, email);
    if (problem) { setError(problem); return; }
    if (!supabaseEnabled) { setError("Sign-in isn't set up on this copy of the app yet."); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { data: { display_name: displayName.trim() || null } },
      });
      if (error) { setError(humanError(error.message)); return; }
      if (data?.session) {
        // Instant session — Supabase project has email confirmation off OR
        // returns a session anyway. Trial starts immediately per spec.
        onAuthed?.(data.session);
      } else {
        // Confirmation required. Spec says non-blocking — we let them know
        // and route to sign-in. The verification banner kicks in only after
        // 7 days if they still haven't confirmed.
        setInfo('Check your inbox to confirm your email. You can sign in now if you want.');
        setTabKey('signin');
      }
    } finally { setLoading(false); }
  };

  const doForgot = async () => {
    reset();
    if (!email) { setError('Enter the email tied to your account.'); return; }
    if (!supabaseEnabled) { setError("Sign-in isn't set up on this copy of the app yet."); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin + (window.location.pathname.startsWith('/app') ? '/app' : '/'),
      });
      if (error) { setError(humanError(error.message)); return; }
      setInfo("If an account exists for that email, we've sent a reset link.");
    } finally { setLoading(false); }
  };

  const submit = (e) => {
    e?.preventDefault?.();
    if (tabKey === 'signin') doSignIn();
    else if (tabKey === 'signup') doSignUp();
    else doForgot();
  };


  const eyebrow = isCreate ? 'Start free' : isSignIn ? 'Welcome back' : 'Reset password';
  const heading = isCreate ? 'Create your account' : isSignIn ? 'Sign in to Empirio' : 'Reset your password';
  const sub = isCreate
    ? 'Set up in under a minute, then jump straight into a lesson.'
    : isSignIn
      ? 'Pick up right where your child left off.'
      : "Enter your email and we'll send you a secure link.";
  const ctaIdle = isCreate ? 'Create account' : isSignIn ? 'Sign in' : 'Send reset link';
  const ctaText = loading ? 'Working…' : ctaIdle;

  return (
    <div className="lumio-auth-wrap" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 26, background: T.bg, position: 'relative', overflow: 'hidden',
      fontFamily: "'Hanken Grotesk', sans-serif",
    }}>
      <style>{`
        .lumio-auth-wrap, .lumio-auth-wrap *{box-sizing:border-box}
        .lumio-auth-wrap a{color:${T.indigo};text-decoration:none}
        .lumio-auth-wrap a:hover{color:#5346c4}
        .lumio-auth-field:focus{outline:none;border-color:${T.indigo};box-shadow:0 0 0 4px rgba(108,92,231,.12)}
        .lumio-auth-field::placeholder{color:#9C99B4}
        @keyframes lumioAuthOrbFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-9px)}}
        @keyframes lumioAuthOrbBlink{0%,44%,52%,100%{transform:scaleY(1)}48%{transform:scaleY(.12)}}
        @keyframes lumioAuthOrbSpin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}
        @keyframes lumioAuthSoftIn{0%{opacity:0;transform:translateY(14px)}100%{opacity:1;transform:translateY(0)}}
        .lumio-auth-card{display:grid;grid-template-columns:1.02fr .98fr}
        @media (max-width:900px){
          .lumio-auth-card{grid-template-columns:1fr}
          .lumio-auth-left{padding:40px 32px !important}
          .lumio-auth-right{padding:36px 28px 32px !important}
          .lumio-auth-orb-big{width:80px !important;height:80px !important;margin-bottom:20px !important}
          .lumio-auth-orb-eyes{gap:12px !important}
          .lumio-auth-orb-eye{width:9px !important;height:17px !important}
        }
      `}</style>

      <div style={{
        position: 'absolute', top: -220, right: -160, width: 640, height: 640, borderRadius: '50%',
        background: 'radial-gradient(circle, #E7E1FB, rgba(231,225,251,0) 70%)',
      }} />
      <div style={{
        position: 'absolute', bottom: -260, left: -180, width: 660, height: 660, borderRadius: '50%',
        background: 'radial-gradient(circle, #FBE6DE, rgba(251,230,222,0) 70%)',
      }} />

      <div className="lumio-auth-card" style={{
        position: 'relative', width: 1120, maxWidth: '100%', background: '#fff',
        border: `1px solid ${T.border}`, borderRadius: 26, overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(30,22,70,.04), 0 24px 60px rgba(40,30,90,.10)',
        animation: 'lumioAuthSoftIn .5s cubic-bezier(.2,.75,.2,1)',
      }}>

        {/* LEFT: brand panel */}
        <div className="lumio-auth-left" style={{
          position: 'relative', padding: '52px 48px',
          background: `linear-gradient(150deg, ${T.indigo} 0%, ${T.indigoDark} 52%, ${T.indigoDeep} 100%)`,
          overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div style={{
            position: 'absolute', top: -90, right: -70, width: 280, height: 280, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,.16), rgba(255,255,255,0) 70%)',
          }} />
          <div style={{
            position: 'absolute', bottom: -120, left: -60, width: 300, height: 300, borderRadius: '50%',
            background: `radial-gradient(circle, rgba(255,122,89,.28), rgba(255,122,89,0) 70%)`,
          }} />

          {/* wordmark */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 38 }}>
            <div style={{ position: 'relative', width: 34, height: 34, flexShrink: 0 }}>
              <div style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'radial-gradient(circle at 34% 28%, #C7BEF6, #6C5CE7 58%, #3A2E8C)',
                boxShadow: '0 4px 12px rgba(20,10,60,.35), inset 0 -3px 8px rgba(42,28,120,.42)',
              }} />
              <div style={{
                position: 'absolute', left: '24%', top: '22%', width: 8, height: 5, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,.9), transparent 70%)',
              }} />
              <div style={{ position: 'absolute', left: 0, top: '46%', width: '100%', display: 'flex', justifyContent: 'center', gap: 5 }}>
                <div style={{ width: 4, height: 7, borderRadius: '50%', background: '#fff' }} />
                <div style={{ width: 4, height: 7, borderRadius: '50%', background: '#fff' }} />
              </div>
            </div>
            <span style={{ fontFamily: "'Space Grotesk'", fontWeight: 700, fontSize: 19, letterSpacing: '-.03em', color: '#fff' }}>Empirio</span>
          </div>

          {/* big Lumi */}
          <div className="lumio-auth-orb-big" style={{
            position: 'relative', alignSelf: 'center', width: 118, height: 118, marginBottom: 28,
            animation: 'lumioAuthOrbFloat 4.6s ease-in-out infinite',
          }}>
            <div style={{
              position: 'absolute', inset: -20, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(199,190,246,.32), rgba(199,190,246,0) 70%)',
            }} />
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: 'radial-gradient(circle at 34% 28%, #C7BEF6, #6C5CE7 58%, #3A2E8C)',
              boxShadow: '0 20px 46px rgba(15,8,50,.42), inset 0 -12px 28px rgba(42,28,120,.45)',
            }} />
            <div style={{
              position: 'absolute', inset: 12, borderRadius: '50%',
              background: 'conic-gradient(from 0deg, transparent, rgba(255,255,255,.26), transparent 40%)',
              animation: 'lumioAuthOrbSpin 7s linear infinite', mixBlendMode: 'screen', opacity: .65,
            }} />
            <div style={{
              position: 'absolute', left: '24%', top: '20%', width: 32, height: 20, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,.88), transparent 70%)', filter: 'blur(1.4px)',
            }} />
            <div className="lumio-auth-orb-eyes" style={{ position: 'absolute', left: 0, top: '46%', width: '100%', display: 'flex', justifyContent: 'center', gap: 18 }}>
              <div className="lumio-auth-orb-eye" style={{ width: 13, height: 25, borderRadius: '50%', background: '#fff', boxShadow: '0 0 12px rgba(255,255,255,.75)', animation: 'lumioAuthOrbBlink 4.8s ease-in-out infinite' }} />
              <div className="lumio-auth-orb-eye" style={{ width: 13, height: 25, borderRadius: '50%', background: '#fff', boxShadow: '0 0 12px rgba(255,255,255,.75)', animation: 'lumioAuthOrbBlink 4.8s ease-in-out infinite' }} />
            </div>
          </div>

          <div style={{ position: 'relative', textAlign: 'center', marginBottom: 34 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: '.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,.62)', fontWeight: 700, marginBottom: 10 }}>Meet your tutor</div>
            <h2 style={{ fontFamily: "'Space Grotesk'", fontWeight: 700, fontSize: 27, lineHeight: 1.2, letterSpacing: '-.025em', color: '#fff', marginBottom: 11 }}>Learn by building,<br />not just answering</h2>
            <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'rgba(255,255,255,.76)', maxWidth: 330, margin: '0 auto' }}>Every lesson gives your child something real to build and test, and Lumi tells them exactly what to fix.</p>
          </div>

          {/* proof points */}
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 15 }}>
            {PROOF_POINTS.map(p => (
              <div key={p.title} style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                <span style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 11, background: 'rgba(255,255,255,.13)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {p.icon}
                </span>
                <div>
                  <div style={{ fontFamily: "'Space Grotesk'", fontWeight: 700, fontSize: 13.5, color: '#fff' }}>{p.title}</div>
                  <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.6)' }}>{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: auth card */}
        <div className="lumio-auth-right" style={{ padding: '52px 52px 44px', display: 'flex', flexDirection: 'column' }}>
          {isForgot && (
            <button onClick={() => { setTabKey('signin'); reset(); }} style={{ ...ghostLink, marginBottom: 18, alignSelf: 'flex-start' }}>
              ← Back to sign in
            </button>
          )}

          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: '.16em', textTransform: 'uppercase', color: T.indigo, fontWeight: 700, marginBottom: 9 }}>{eyebrow}</div>
          <h1 style={{ fontFamily: "'Space Grotesk'", fontWeight: 700, fontSize: 30, letterSpacing: '-.03em', color: T.ink, marginBottom: 7 }}>{heading}</h1>
          <p style={{ fontSize: 14.5, color: T.muted, lineHeight: 1.55, marginBottom: 26 }}>{sub}</p>

          {!isForgot && (
            <div style={{ display: 'flex', gap: 6, background: '#F4F3F9', border: `1px solid ${T.border}`, borderRadius: 14, padding: 5, marginBottom: 26 }}>
              <button onClick={() => { setTabKey('signin'); reset(); }} style={tabBtnStyle(isSignIn)}>Sign in</button>
              <button onClick={() => { setTabKey('signup'); reset(); }} style={tabBtnStyle(isCreate)}>Create account</button>
            </div>
          )}

          <form onSubmit={submit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {isCreate && (
                <label style={{ display: 'block' }}>
                  <span style={label}>Your name</span>
                  <input className="lumio-auth-field" type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
                    placeholder="Your name" autoComplete="name" style={field} />
                </label>
              )}
              <label style={{ display: 'block' }}>
                <span style={label}>Email</span>
                <input className="lumio-auth-field" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" autoComplete="email" style={field} required />
              </label>
              {!isForgot && (
                <label style={{ display: 'block' }}>
                  <span style={{ ...label, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span>Password</span>
                    {isSignIn && (
                      <button type="button" onClick={() => { setTabKey('forgot'); reset(); }} style={{ ...ghostLink, fontSize: 12 }}>Forgot?</button>
                    )}
                  </span>
                  <input className="lumio-auth-field" type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder={isCreate ? `At least ${PASSWORD_MIN} characters` : 'Your password'}
                    autoComplete={isCreate ? 'new-password' : 'current-password'}
                    style={field} required />
                  {isCreate && <span style={{ display: 'block', fontSize: 11.5, color: T.muted, marginTop: 6 }}>{PASSWORD_HINT}</span>}
                </label>
              )}
            </div>

            {error && <div style={{ fontSize: 13, color: T.error, marginTop: 13, fontWeight: 600 }}>{error}</div>}
            {info && <div style={{ fontSize: 13, color: T.green, marginTop: 13, fontWeight: 600 }}>{info}</div>}

            <button type="submit" disabled={loading} style={submitBtnStyle(loading)}>
              {ctaText}
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M5 12 h13 M13 6 l6 6 -6 6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </form>

          {isCreate && (
            <p style={{ marginTop: 13, fontSize: 12, color: T.mutedLo, lineHeight: 1.5, textAlign: 'center' }}>Free to start. No card needed.<br />By creating an account you agree to our <a href="https://empirio.com.au/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>terms of use</a> and <a href="https://empirio.com.au/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>privacy policy</a>.</p>
          )}

          {/* what you get */}
          <div style={{ marginTop: 28, paddingTop: 22, borderTop: `1px solid ${T.fieldTop}` }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: T.mutedLo, fontWeight: 700, marginBottom: 14 }}>What you get</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              {WHAT_YOU_GET.map(w => (
                <div key={w.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
                  <span style={{ flexShrink: 0, width: 31, height: 31, borderRadius: 10, background: w.tile, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {w.icon}
                  </span>
                  <div>
                    <div style={{ fontFamily: "'Space Grotesk'", fontWeight: 700, fontSize: 13, color: T.ink }}>{w.title}</div>
                    <div style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.45 }}>{w.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>

    </div>
  );
}

// Friendly translations for the most common Supabase auth errors.
function humanError(msg) {
  if (!msg) return 'Something went wrong. Please try again.';
  const m = msg.toLowerCase();
  if (m.includes('invalid login credentials')) return 'Email or password is incorrect.';
  if (m.includes('email not confirmed'))       return 'Check your inbox. Your email needs to be confirmed first.';
  if (m.includes('user already registered'))    return 'An account already exists for this email. Try signing in.';
  if (m.includes('rate limit'))                 return "Too many attempts. Please wait a minute and try again.";
  if (m.includes('weak password'))              return 'That password is too weak. Use at least 8 characters with a mix of letters and numbers.';
  return msg;
}
